import { booleanAttribute } from "#ce/utils/props";

export const props = Object.freeze({
  /**
   * Id of the native `<select>` this control mirrors. The element reads its options and
   * current value from that select, and writes the chosen value back to it — dispatching a
   * bubbling change event on the select's form so the surrounding search refreshes.
   *
   * Without it the element owns the sort: its `<option>` children are the options, its `value`
   * property is the current value, and a pick dispatches a bubbling `change` event on the element.
   *
   * @type {String}
   */
  selectId: {
    type: String,
    default: "",
  },
  /**
   * CSS selector for the near/location filter element. Selecting the distance sort without a
   * location opens that filter and scrolls it into view; filling a location in it closes this
   * control. Without it, choosing the distance sort opens nothing.
   *
   * @type {String}
   */
  nearFilterSelector: {
    type: String,
    default: "",
  },
  /**
   * Heading of the sort dialog.
   *
   * @type {String}
   * @default "Sort results by"
   */
  dialogTitle: {
    type: String,
    default: "Sort results by",
  },
  /**
   * Label of the dialog's confirm button.
   *
   * @type {String}
   * @default "Done"
   */
  closeText: {
    type: String,
    default: "Done",
  },
  /**
   * Prefix shown before the selected option on the trigger, as "Sort: Recommended". Pass an
   * empty string to show the option label alone.
   *
   * @type {String}
   * @default "Sort"
   */
  triggerPrefix: {
    type: String,
    default: "Sort",
  },
  /**
   * `data-testid` of the trigger button.
   *
   * @type {String}
   */
  toggleTestId: {
    type: String,
    default: "",
  },
  /**
   * `data-testid` of each option label, which also carries the option's value as `sort-label-name`.
   *
   * @type {String}
   */
  optionTestId: {
    type: String,
    default: "",
  },
  /**
   * URL of an image whose shape is drawn after the trigger label in the trigger's icon colour, in
   * place of the default chevron icon.
   *
   * @type {String}
   */
  caretImage: {
    type: String,
    default: "",
  },
  /**
   * Shows the keyboard focus ring on the option focused when the dialog opens.
   *
   * @type {Boolean|String}
   */
  focusRing: booleanAttribute(),
  /**
   * Places the dialog below the bottom of the element rather than below the trigger button.
   *
   * @type {Boolean|String}
   */
  anchorHost: booleanAttribute(),
  /**
   * Disables the control: it does not open or change by user input, and an ancestor
   * `<fieldset disabled>` disables it as well. A script can still write `value`.
   *
   * @type {Boolean|String}
   */
  disabled: booleanAttribute(),
  /**
   * Sizes the trigger to its content on small screens, instead of the compact control height of
   * the theme.
   *
   * @type {Boolean|String}
   */
  autoHeight: booleanAttribute(),
});
